import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Dialog } from '@components/dialog';
import * as styles from '@components/modals/Modal.styles';
import { FormSection, Form } from '@components/forms';
import { getFormFields } from './getFormFields';

export type ConfirmEmailModalFormValues = {
  email: string;
};

export type ConfirmEmailModalProps = {
  isOpen: boolean;
  currentEmail: string;
  onCloseModal: () => void;
  onConfirm: (email: string) => Promise<void>;
  isLoading?: boolean;
  isBindPage?: boolean;
  isRequired?: boolean;
};

export const ConfirmEmailModal: React.FC<ConfirmEmailModalProps> = ({
  isOpen,
  currentEmail,
  onCloseModal,
  onConfirm,
  isBindPage,
  isLoading = false,
  isRequired = true,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<ConfirmEmailModalFormValues>({
    defaultValues: {
      email: currentEmail,
    },
    mode: 'onChange',
  });

  const handleReset = methods.reset;

  // Update form when currentEmail prop changes
  React.useEffect(() => {
    if (currentEmail) {
      handleReset({
        email: currentEmail,
      });
    }
  }, [currentEmail, handleReset]);

  const handleExit = () => {
    clearForm();
    onCloseModal();
  };

  const clearForm = () => {
    methods.reset({
      email: currentEmail,
    });
  };

  const handleConfirmEmail = async () => {
    const formData = methods.getValues();
    if (!formData.email && isRequired) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(formData.email || '');
      clearForm();
      onCloseModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  const fields = useMemo(() => {
    return getFormFields({ isRequired, defaultValue: currentEmail });
  }, [isRequired, currentEmail]);

  const isValid = methods.formState.isValid && (isRequired ? methods.watch('email') !== '' : true);

  const email = methods.watch('email');

  let buttonLabel = isRequired || !!email ? 'Confirm Email' : 'Confirm with No Email';
  if (isBindPage) {
    buttonLabel = isRequired || !!email ? 'Confirm Email & Bind' : 'Confirm with No Email & Bind';
  }

  return (
    <Dialog
      handleCloseDialog={handleExit}
      isPrimaryBtnDisabled={!isValid}
      isPrimaryBtnLoading={isLoading || isSubmitting}
      open={isOpen}
      primaryBtnText={buttonLabel}
      primaryBtnAction={handleConfirmEmail}
      secondaryBtnAction={handleExit}
      secondaryBtnText='Exit'
      title='Confirm Email Address'
    >
      <Form methods={methods} formId='confirm-email-modal-form'>
        <Box sx={{ ...styles.modalContent, paddingTop: 0, paddingBottom: 0 }}>
          <Box sx={styles.modalScrollContainer}>
            <Typography variant='body2' sx={{ mt: 2, fontWeight: 'normal', color: 'text.secondary' }}>
              Confirm the customer email address is still correct.
            </Typography>
            <FormSection fields={fields} styleConfig={{ spacing: 3 }} />
          </Box>
        </Box>
      </Form>
    </Dialog>
  );
};
